/**
 * Shared condition operator evaluation logic.
 *
 * Used by both EntityForm and DataList for evaluating conditions
 * against data values. Implements all 11 condition operators.
 *
 * @see EntityForm spec sections 6.1-6.2 for condition definitions.
 */

// ---------------------------------------------------------------------------
// Condition Operator Type
// ---------------------------------------------------------------------------

/**
 * Supported comparison operators for condition evaluation.
 */
export type ConditionOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'greaterThan'
  | 'lessThan'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'in'
  | 'notIn'
  | 'matches';

// ---------------------------------------------------------------------------
// Single-value emptiness check
// ---------------------------------------------------------------------------

/**
 * Determines whether a value is considered "empty" for condition evaluation.
 * Empty means: null, undefined, empty string, or empty array.
 */
function isValueEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Operator implementations
// ---------------------------------------------------------------------------

/**
 * Map of all 11 condition operators to their evaluation functions.
 *
 * Each function receives the current field value and the condition's
 * comparison value, returning true when the condition is met.
 */
const operators: Record<
  ConditionOperator,
  (fieldValue: unknown, conditionValue: unknown) => boolean
> = {
  equals(fieldValue, conditionValue) {
    return fieldValue === conditionValue;
  },

  notEquals(fieldValue, conditionValue) {
    return fieldValue !== conditionValue;
  },

  contains(fieldValue, conditionValue) {
    if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
      return fieldValue.includes(conditionValue);
    }
    if (Array.isArray(fieldValue)) {
      return fieldValue.includes(conditionValue);
    }
    return false;
  },

  notContains(fieldValue, conditionValue) {
    return !operators.contains(fieldValue, conditionValue);
  },

  greaterThan(fieldValue, conditionValue) {
    if (typeof fieldValue === 'number' && typeof conditionValue === 'number') {
      return fieldValue > conditionValue;
    }
    return false;
  },

  lessThan(fieldValue, conditionValue) {
    if (typeof fieldValue === 'number' && typeof conditionValue === 'number') {
      return fieldValue < conditionValue;
    }
    return false;
  },

  isEmpty(fieldValue, _conditionValue) {
    return isValueEmpty(fieldValue);
  },

  isNotEmpty(fieldValue, _conditionValue) {
    return !isValueEmpty(fieldValue);
  },

  in(fieldValue, conditionValue) {
    if (!Array.isArray(conditionValue)) return false;
    return conditionValue.includes(fieldValue);
  },

  notIn(fieldValue, conditionValue) {
    if (!Array.isArray(conditionValue)) return true;
    return !conditionValue.includes(fieldValue);
  },

  matches(fieldValue, conditionValue) {
    if (typeof fieldValue !== 'string' || typeof conditionValue !== 'string') {
      return false;
    }
    if (conditionValue.length > 200) {
      return false;
    }
    try {
      const regex = new RegExp(conditionValue);
      return regex.test(fieldValue);
    } catch {
      return false;
    }
  },
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Evaluates a single condition operator against a field value.
 *
 * This is the shared entry point used by both EntityForm (for conditional
 * field logic) and DataList (for row/column condition evaluation).
 *
 * @param fieldValue - The current value of the field being evaluated.
 * @param operator - The comparison operator to apply.
 * @param conditionValue - The value to compare against.
 * @returns `true` if the condition is satisfied, `false` otherwise.
 */
export function evaluateOperator(
  fieldValue: unknown,
  operator: ConditionOperator,
  conditionValue: unknown,
): boolean {
  const operatorFn = operators[operator];
  return operatorFn(fieldValue, conditionValue);
}
